'use client';

import { useCallback, useState } from 'react';

import type {
  TimeFieldProps as AriaTimeFieldProps,
  TimeValue,
} from 'react-aria-components';
import {
  Label,
  DateInput as AriaDateInput,
  DateSegment as AriaDateSegment,
  TimeField as AriaTimeField,
} from 'react-aria-components';

import type { TimePeriod } from '@avodah-inc/types';
import { cn, floatingLabelStyles } from '@avodah-inc/ui/utils';

import { Select } from '../select/select';

export type TimeValues = TimeValue;

export type TimeFieldProps = Omit<AriaTimeFieldProps<TimeValue>, 'onChange'> & {
  label?: string;
  defaultPeriod?: TimePeriod | null;
  onChange?: (value: string) => void;
  isInvalid?: boolean;
  dataTestId?: string;
  dataTestIdForAMPM?: string;
};

export function TimeField({
  label,
  onChange,
  isInvalid,
  ...props
}: TimeFieldProps) {
  const [period, setPeriod] = useState<TimePeriod>(props.defaultPeriod ?? 'am');

  const calculateTime = useCallback(
    (value: TimeValue, timePeriod: TimePeriod): TimeValue => {
      if (!value.hour && value.hour !== 0) return value;

      let hours = value.hour;

      if (hours === 12) {
        hours = timePeriod === 'pm' ? 12 : 0;
      } else if (timePeriod === 'pm' && hours < 12) {
        hours = hours + 12;
      } else if (timePeriod === 'am' && hours > 12) {
        hours = hours - 12;
      }

      return {
        hour: hours,
        minute: value.minute || 0,
        second: 0,
        millisecond: 0,
      } as TimeValue;
    },
    [],
  );

  const [time, setTime] = useState<TimeValue | undefined>(
    props.defaultValue ? calculateTime(props.defaultValue, period) : undefined,
  );

  const handleTimeChange: AriaTimeFieldProps<TimeValue>['onChange'] = (
    value,
  ) => {
    const adjustedTime = calculateTime(value as unknown as TimeValue, period);

    const hours = adjustedTime.hour.toString().padStart(2, '0');
    const minutes = adjustedTime.minute.toString().padStart(2, '0');
    const timeString = `${hours}:${minutes}:00`;

    setTime(adjustedTime);
    onChange?.(timeString);
  };

  const periodItems = [
    { label: 'AM', value: 'am' },
    { label: 'PM', value: 'pm' },
  ];

  return (
    <AriaTimeField
      hourCycle={12}
      {...props}
      onChange={handleTimeChange}
      className={cn(
        'text-body-3 focus:outline-primary-300 data-[invalid=true]:border-red focus:border-primary group relative flex h-10 w-full items-center justify-between gap-2 rounded border border-gray-300 bg-white py-2 pl-4 text-gray-900 focus:outline-1 focus:outline-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=open]:rounded-b-none data-[state=open]:border-b-0',
        isInvalid && 'border-red-500',
      )}
      data-testid={props.dataTestId}
    >
      {label && <Label className={floatingLabelStyles}>{label}</Label>}
      <AriaDateInput className="flex outline-none">
        {(segment) =>
          segment.type === 'dayPeriod' ? (
            <></>
          ) : (
            <AriaDateSegment
              segment={segment}
              className={cn(
                'px-1 text-sm outline-none',
                'data-[type=dayPeriod]:text-primary data-[type=dayPeriod]:px-0 data-[type=dayPeriod]:font-semibold data-[type=dayPeriod]:uppercase',
              )}
            />
          )
        }
      </AriaDateInput>

      <Select
        value={[period]}
        items={periodItems}
        showCheckIcon={false}
        mainAxisOffset={2}
        dataTestId={props.dataTestIdForAMPM}
        classNames={{
          trigger: '!border-none !rounded text-sm text-blue',
          text: 'text-primary font-semibold uppercase',
          content: '!rounded',
        }}
        clearable={false}
        onValueChange={({ value }) => {
          setPeriod(value as TimePeriod);
          if (time) {
            const adjustedTime = calculateTime(time, value as TimePeriod);
            const hours = adjustedTime.hour.toString().padStart(2, '0');
            const minutes = adjustedTime.minute.toString().padStart(2, '0');
            const timeString = `${hours}:${minutes}:00`;
            onChange?.(timeString);
          }
        }}
      />
    </AriaTimeField>
  );
}
